<template>
  <div>
    <!-- Rendered inside VerificationsWorkspace, which owns the title and tabs. -->
    <div class="page-section pt-0">
      <div :class="containerClass">
        <div class="card mb-0">
          <div class="card-header">
            <div class="row align-items-center" style="white-space: nowrap">
              <!-- Search -->
              <div class="col-lg-auto">
                <form class="search-form search-form--light d-lg-inline-flex mb-8pt mb-lg-0" @submit.prevent="">
                  <b-form-input
                    class="w-lg-auto"
                    placeholder="Search by participant or exam"
                    v-model="searchTerm"
                    @input="onSearch"
                  />
                  <b-btn variant="flush" type="submit">
                    <md-icon v-text="'search'" />
                  </b-btn>
                </form>
              </div>
            </div>
          </div>

          <!-- Exam Results Table (read-only for organizations) -->
          <b-table
            :fields="tableColumns"
            :items="examResults"
            :busy="isLoading"
            head-variant="light"
            class="table-nowrap"
            hover
            responsive
            no-local-sorting
            show-empty
            empty-text="No exam results yet."
            @sort-changed="onSortChange"
          >
            <template #cell(student)="data">
              <strong>{{ get(data.item, 'student.user.first_name') }} {{ get(data.item, 'student.user.last_name') }}</strong>
            </template>

            <template #cell(school)="data">
              {{ data.item.school_name || '—' }}
            </template>

            <template #cell(test_provider)="data">
              {{ get(data.item, 'test_provider.name', '—') }}
            </template>

            <template #cell(theory)="data">
              <template v-if="data.item.theory_result">
                <b-badge
                  variant="none"
                  :class="`border border-${data.item.theory_result === 'pass' ? 'success' : 'danger'} text-${
                    data.item.theory_result === 'pass' ? 'success' : 'danger'
                  }`"
                  >{{ data.item.theory_result === 'pass' ? 'Pass' : 'Fail' }}</b-badge
                >
                <small v-if="data.item.theory_date" class="text-muted ml-1">{{ data.item.theory_date }}</small>
              </template>
              <span v-else>—</span>
            </template>

            <template #cell(skill)="data">
              <template v-if="data.item.skill_result">
                <b-badge
                  variant="none"
                  :class="`border border-${data.item.skill_result === 'pass' ? 'success' : 'danger'} text-${
                    data.item.skill_result === 'pass' ? 'success' : 'danger'
                  }`"
                  >{{ data.item.skill_result === 'pass' ? 'Pass' : 'Fail' }}</b-badge
                >
                <small v-if="data.item.skill_date" class="text-muted ml-1">{{ data.item.skill_date }}</small>
              </template>
              <span v-else>—</span>
            </template>

            <template #cell(overall_status)="data">
              <b-badge
                :class="`border border-${statusVariant(data.item.overall_status)} text-${statusVariant(
                  data.item.overall_status
                )}`"
                variant="none"
                >{{ statusLabel(data.item.overall_status) }}</b-badge
              >
            </template>

            <template #cell(submitted_at)="data">
              {{ data.item.submitted_at || '—' }}
            </template>
          </b-table>

          <!-- Footer Pagination -->
          <div class="card-footer">
            <pagination
              v-model="currentPage"
              :total-rows="totalExamResults"
              :per-page="perPage"
              @change="onPageChange"
              aria-controls="exam-results-table"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { debounce, get } from 'lodash';
import { mapActions } from 'vuex';
import Page from '@/components/Page.vue';
import Pagination from '../../../components/Ui/Pagination.vue';
import MdIcon from '../../../components/Ui/MdIcon.vue';
import { DEFAULT_PAGE_SIZE } from '../../../common/constants';

const STATUS_META = {
  not_started: { label: 'Not Started', variant: 'secondary' },
  in_progress: { label: 'In Progress', variant: 'warning' },
  passed: { label: 'Passed', variant: 'success' },
  failed: { label: 'Failed', variant: 'danger' },
};

export default {
  components: {
    MdIcon,
    Pagination,
  },
  extends: Page,

  data() {
    return {
      title: 'Exam Results',
      searchTerm: '',
      isLoading: false,
      perPage: DEFAULT_PAGE_SIZE,
      currentPage: 1,
      totalExamResults: 0,
      ordering: null,
      examResults: [],
    };
  },

  computed: {
    tableColumns() {
      return [
        { key: 'student', label: 'Participant' },
        { key: 'school', label: 'School' },
        { key: 'test_provider', label: 'Exam' },
        { key: 'theory', label: 'Theory' },
        { key: 'skill', label: 'Skill' },
        { key: 'overall_status', label: 'Overall', sortable: true },
        { key: 'submitted_at', label: 'Submitted', sortable: true },
      ];
    },
  },

  methods: {
    ...mapActions('examResult', ['getAllExamResults']),
    get,

    statusVariant(status) {
      return get(STATUS_META, `${status}.variant`, 'secondary');
    },
    statusLabel(status) {
      return get(STATUS_META, `${status}.label`, status);
    },

    async fetchExamResults(pageNum = 1, params = {}) {
      this.isLoading = true;
      document.getElementById('app').scrollIntoView();

      try {
        const response = await this.getAllExamResults({
          limit: this.perPage,
          offset: (pageNum - 1) * this.perPage,
          ...(this.ordering && { ordering: this.ordering }),
          ...(this.searchTerm && { search: this.searchTerm }),
          ...params,
        });
        this.examResults = response.data.results;
        this.currentPage = pageNum;
        this.totalExamResults = response.data.count;
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isLoading = false;
    },

    onPageChange(pageNum) {
      this.fetchExamResults(pageNum);
    },

    onSortChange(context) {
      this.ordering = context.sortDesc ? '-' + context.sortBy : context.sortBy;
      this.fetchExamResults();
    },

    onSearch() {
      this.debouncedSearchExamResults(this);
    },

    debouncedSearchExamResults: debounce((vm) => {
      vm.fetchExamResults();
    }, 500),
  },

  async mounted() {
    await this.fetchExamResults();
  },
};
</script>
